/**
 * Shared WebGL utility functions for 3D texture operations
 * used by GPU-accelerated SDF renderers (anisotropic margin,
 * Gaussian smoothing, etc.).
 */

import { gl } from '../../../Engine';
import { FrameBufferObject } from '../../../core/utils/FrameBufferObject';
import { UniformFloat } from '../../../core/utils/uniforms/UniformFloat';
import { maybeYield } from './utils';

/**
 * Create an empty R8 3D texture with LINEAR filtering
 * and CLAMP_TO_EDGE wrapping.
 */
export function createSdfTexture3D(
    nx: number,
    ny: number,
    nz: number
): WebGLTexture | null {
    const texture = gl.createTexture();
    if (!texture) {
        return null;
    }

    gl.bindTexture(gl.TEXTURE_3D, texture);
    gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_R, gl.CLAMP_TO_EDGE);
    gl.texImage3D(
        gl.TEXTURE_3D,
        0,
        gl.R8,
        nx,
        ny,
        nz,
        0,
        gl.RED,
        gl.UNSIGNED_BYTE,
        null
    );
    gl.bindTexture(gl.TEXTURE_3D, null);

    return texture;
}

/**
 * Copy a source 3D texture to a destination 3D texture
 * slice-by-slice via readback + upload. Handles 4-byte
 * row alignment for texSubImage3D.
 */
export async function copyTexture3DAsync(
    src: WebGLTexture,
    dst: WebGLTexture,
    nx: number,
    ny: number,
    nz: number,
    lastYield: number,
    signal?: AbortSignal
): Promise<number> {
    const srcFbo = gl.createFramebuffer();
    const bytesPerPixel = 4;
    const sliceSize = nx * ny;
    const slicePixels = new Uint8Array(sliceSize * bytesPerPixel);
    const sliceData = new Uint8Array(sliceSize);

    for (let z = 0; z < nz; z++) {
        gl.bindFramebuffer(gl.FRAMEBUFFER, srcFbo);
        gl.framebufferTextureLayer(
            gl.FRAMEBUFFER,
            gl.COLOR_ATTACHMENT0,
            src,
            0,
            z
        );
        gl.readPixels(0, 0, nx, ny, gl.RGBA, gl.UNSIGNED_BYTE, slicePixels);

        for (let i = 0; i < sliceSize; i++) {
            sliceData[i] = slicePixels[i * bytesPerPixel];
        }

        gl.bindTexture(gl.TEXTURE_3D, dst);
        gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
        gl.texSubImage3D(
            gl.TEXTURE_3D,
            0,
            0,
            0,
            z,
            nx,
            ny,
            1,
            gl.RED,
            gl.UNSIGNED_BYTE,
            sliceData
        );

        lastYield = await maybeYield(lastYield, signal);
    }

    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 4);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.deleteFramebuffer(srcFbo);
    gl.bindTexture(gl.TEXTURE_3D, null);

    return lastYield;
}

/**
 * Read a 3D texture back to per-slice Uint8Array[].
 * Runs synchronously to avoid GL state corruption from
 * yielding while a framebuffer is bound.
 */
export function readTexture3D(
    texture: WebGLTexture,
    nx: number,
    ny: number,
    nz: number,
    signal?: AbortSignal
): Uint8Array[] {
    const result: Uint8Array[] = new Array(nz);
    const fb = gl.createFramebuffer();
    const bytesPerPixel = 4;
    const sliceSize = nx * ny;
    const slicePixels = new Uint8Array(sliceSize * bytesPerPixel);

    gl.bindFramebuffer(gl.FRAMEBUFFER, fb);

    for (let z = 0; z < nz; z++) {
        if (signal?.aborted) {
            gl.bindFramebuffer(gl.FRAMEBUFFER, null);
            gl.deleteFramebuffer(fb);
            throw new DOMException('Operation aborted', 'AbortError');
        }

        gl.framebufferTextureLayer(
            gl.FRAMEBUFFER,
            gl.COLOR_ATTACHMENT0,
            texture,
            0,
            z
        );
        gl.readPixels(0, 0, nx, ny, gl.RGBA, gl.UNSIGNED_BYTE, slicePixels);

        const slice = new Uint8Array(sliceSize);
        for (let i = 0; i < sliceSize; i++) {
            slice[i] = slicePixels[i * bytesPerPixel];
        }
        result[z] = slice;
    }

    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.deleteFramebuffer(fb);

    return result;
}

/**
 * Read back an R8 FBO and write the pixels into a 3D
 * texture along a non-Z axis (coronal Y=1 or sagittal X=0).
 *
 * @param fbo       The FBO whose colour attachment holds the
 *                  rendered 2D SDF slice.
 * @param target    The destination 3D texture.
 * @param sliceAxis 0 (sagittal) or 1 (coronal).
 * @param sliceIndex Voxel index along the slice axis.
 * @param width     Planar width of the FBO (pixels).
 * @param height    Planar height of the FBO (pixels).
 */
export function copyFboToTexture3DSlice(
    fbo: FrameBufferObject,
    target: WebGLTexture,
    sliceAxis: 0 | 1,
    sliceIndex: number,
    width: number,
    height: number
): Uint8Array {
    // Read R8 FBO directly via gl.RED (1 byte/pixel),
    // same pattern as SignedDistanceField.sliceToArrayInternal.
    fbo.bind();
    const pixelCount = width * height;
    const pixels = new Uint8Array(pixelCount);
    gl.pixelStorei(gl.PACK_ALIGNMENT, 1);
    gl.readPixels(0, 0, width, height, gl.RED, gl.UNSIGNED_BYTE, pixels);
    gl.pixelStorei(gl.PACK_ALIGNMENT, 4);
    fbo.unbind();

    gl.bindTexture(gl.TEXTURE_3D, target);
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);

    if (sliceAxis === 1) {
        // Coronal: slab of (width, 1, height) at y = sliceIndex.
        gl.texSubImage3D(
            gl.TEXTURE_3D,
            0,
            0,
            sliceIndex,
            0,
            width,
            1,
            height,
            gl.RED,
            gl.UNSIGNED_BYTE,
            pixels
        );
    } else {
        // Sagittal: slab of (1, width, height) at x = sliceIndex.
        gl.texSubImage3D(
            gl.TEXTURE_3D,
            0,
            sliceIndex,
            0,
            0,
            1,
            width,
            height,
            gl.RED,
            gl.UNSIGNED_BYTE,
            pixels
        );
    }

    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 4);
    gl.bindTexture(gl.TEXTURE_3D, null);

    return pixels;
}

/**
 * Render a per-slice FBO pass over all Z slices of a 3D
 * texture. Runs synchronously to avoid GL state corruption
 * from yielding while shader, VAO, and texture bindings are
 * active. Expects the caller to have already bound a quad
 * VAO (via RenderStorage.getQuadVAO().bind(0)) and started
 * the shader program.
 */
export function renderSlicePass(
    srcTex: WebGLTexture,
    dstTex: WebGLTexture,
    fbo: FrameBufferObject,
    sliceZUniform: UniformFloat,
    nz: number,
    signal?: AbortSignal
): void {
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_3D, srcTex);

    for (let z = 0; z < nz; z++) {
        if (signal?.aborted) {
            throw new DOMException('Operation aborted', 'AbortError');
        }

        sliceZUniform.loadFloat((z + 0.5) / nz);

        fbo.bind();
        fbo.createTextureLayer(dstTex, z);

        gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    }
}
